import React, { useCallback, useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { fetchPostComments, createComment } from "../../services/api";
import styles from "./PostComments.module.css";

/**
 * Decodifica um JWT sem verificar a assinatura (apenas para exibição no frontend).
 * A verificação real da assinatura acontece no backend.
 */
function decodeJwt(token) {
    try {
        const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
}

function formatDate(value) {
    if (!value) return "";
    const normalized = value.endsWith("Z") ? value : `${value.replace("+00:00", "")}Z`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" }).format(date);
}

function CommentItem({ comment }) {
    return (
        <div className={styles.comment}>
            <div className={styles.commentHeader}>
                {comment.user_picture ? (
                    <img
                        src={comment.user_picture}
                        alt={comment.user_name}
                        className={styles.commentAvatar}
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <div className={styles.commentAvatarFallback}>
                        {(comment.user_name || "?")[0].toUpperCase()}
                    </div>
                )}
                <div className={styles.commentMeta}>
                    <strong className={styles.commentName}>{comment.user_name}</strong>
                    <time className={styles.commentDate}>{formatDate(comment.created_at)}</time>
                </div>
            </div>
            <p className={styles.commentText}>{comment.content}</p>
        </div>
    );
}

export default function PostComments({ slug }) {
    const [user, setUser] = useState(null);
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(true);
    const [commentText, setCommentText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [justPosted, setJustPosted] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [authError, setAuthError] = useState("");

    const loadComments = useCallback(async () => {
        try {
            setLoadingComments(true);
            const data = await fetchPostComments(slug);
            setComments(data.comments || []);
        } catch {
            // comentários não são críticos — falha silenciosa
        } finally {
            setLoadingComments(false);
        }
    }, [slug]);

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    function handleGoogleSuccess(credentialResponse) {
        const decoded = decodeJwt(credentialResponse.credential);
        if (!decoded) {
            setAuthError("Não foi possível ler as informações do usuário.");
            return;
        }
        setAuthError("");
        setUser({
            name: decoded.name || "Usuário",
            picture: decoded.picture || "",
            email: decoded.email || "",
            token: credentialResponse.credential,
        });
    }

    function handleGoogleError() {
        setAuthError("Falha ao autenticar com Google. Tente novamente.");
    }

    function handleLogout() {
        setUser(null);
        setJustPosted(false);
        setCommentText("");
        setSubmitError("");
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!commentText.trim() || !user || submitting) return;
        setSubmitting(true);
        setSubmitError("");
        try {
            const res = await createComment(slug, user.token, commentText.trim());
            setCommentText("");
            if (res?.comment) {
                setComments((prev) => [...prev, res.comment]);
            } else {
                loadComments();
            }
            setJustPosted(true);
        } catch (err) {
            setSubmitError(
                err?.message || "Falha ao enviar comentário. Tente novamente."
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section className={styles.section}>
            <h2 className={styles.sectionTitle}>💬 Comentários</h2>

            {/* ── Área de autenticação / formulário ── */}
            <div className={styles.formArea}>
                {!user ? (
                    <div className={styles.loginPrompt}>
                        <p className={styles.loginText}>
                            Entre com sua conta Google para deixar um comentário.
                        </p>
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            text="signin_with"
                            shape="rectangular"
                            locale="pt_BR"
                            width="240"
                        />
                        {authError && (
                            <p className={styles.errorMsg}>{authError}</p>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {justPosted && (
                            <div className={styles.successBox}>
                                <span className={styles.successIcon}>✅</span>
                                <div>
                                    <strong>Comentário publicado!</strong>
                                    <p>Obrigado por participar. Quer comentar mais?</p>
                                </div>
                            </div>
                        )}
                        <div className={styles.userRow}>
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt={user.name}
                                    className={styles.userAvatar}
                                    referrerPolicy="no-referrer"
                                />
                            ) : (
                                <div className={styles.userAvatarFallback}>
                                    {user.name[0].toUpperCase()}
                                </div>
                            )}
                            <span className={styles.userName}>{user.name}</span>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className={styles.logoutBtn}
                            >
                                Sair
                            </button>
                        </div>
                        <textarea
                            className={styles.textarea}
                            placeholder="Escreva seu comentário..."
                            value={commentText}
                            onChange={(e) => {
                                setCommentText(e.target.value);
                                if (justPosted) setJustPosted(false);
                            }}
                            rows={4}
                            maxLength={2000}
                            disabled={submitting}
                        />
                        <div className={styles.formFooter}>
                            <span className={styles.charCount}>
                                {commentText.length}/2000
                            </span>
                            {submitError && (
                                <p className={styles.errorMsg}>{submitError}</p>
                            )}
                            <button
                                type="submit"
                                className={styles.submitBtn}
                                disabled={!commentText.trim() || submitting}
                            >
                                {submitting ? "Enviando..." : "Enviar comentário"}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* ── Lista de comentários aprovados ── */}
            <div className={styles.commentsList}>
                {loadingComments ? (
                    <div className={styles.loadingRow}>
                        <div className={styles.loadingDot} />
                        <div className={styles.loadingDot} />
                        <div className={styles.loadingDot} />
                    </div>
                ) : comments.length === 0 ? (
                    <p className={styles.emptyText}>
                        Nenhum comentário ainda. Seja o primeiro a comentar!
                    </p>
                ) : (
                    <div className={styles.commentsInner}>
                        <p className={styles.commentsCount}>
                            {comments.length}{" "}
                            {comments.length === 1 ? "comentário" : "comentários"}
                        </p>
                        {comments.map((comment) => (
                            <CommentItem key={comment.id} comment={comment} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
